import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ArrowLeft,
    Play,
    CheckCircle,
    Clock,
    AlertCircle,
    Code,
    Database,
    Zap,
    Globe,
    Shield,
    Cpu,
    Layers,
    X
} from 'lucide-react'

const BACKEND_URL = 'https://deepnx-simple-backend.speedofmastry.workers.dev'

const PipelineStreamer = ({ chatPlan, onComplete, onBack }) => {
    const [isStreaming, setIsStreaming] = useState(false)
    const [pipelineStatus, setPipelineStatus] = useState({})
    const [activePopouts, setActivePopouts] = useState([])
    const [sessionId, setSessionId] = useState(null)
    const [completedPipelines, setCompletedPipelines] = useState([])
    const [currentPipeline, setCurrentPipeline] = useState(null)
    const [pipelineResults, setPipelineResults] = useState([])
    const eventSourceRef = useRef(null)

    // 🤖 AI-POWERED INTELLIGENT PIPELINES WITH DEEPSEEK INTEGRATION
    const pipelineConfig = [
        { id: '01', name: 'AI Frontend Generator', icon: <Code className="w-5 h-5" />, color: 'bg-blue-500', description: 'AI agent that generates complete React/Vue/Angular apps', url: 'https://ai-pipeline-01-frontend-framework-generator.speedofmastry.workers.dev', aiPowered: true },
        { id: '02', name: 'AI Backend Generator', icon: <Layers className="w-5 h-5" />, color: 'bg-green-500', description: 'AI agent that creates robust Node.js/Express APIs', url: 'https://ai-pipeline-02-backend-api-generator.speedofmastry.workers.dev', aiPowered: true },
        { id: '03', name: 'AI Database Designer', icon: <Database className="w-5 h-5" />, color: 'bg-purple-500', description: 'AI agent that designs optimized database schemas', url: 'https://ai-pipeline-03-database-schema-designer.speedofmastry.workers.dev', aiPowered: true },
        { id: '04', name: 'AI Auth System Builder', icon: <Shield className="w-5 h-5" />, color: 'bg-red-500', description: 'AI agent that implements secure authentication', url: 'https://ai-pipeline-04-authentication-system-builder.speedofmastry.workers.dev', aiPowered: true },
        { id: '05', name: 'AI Real-time Builder', icon: <Zap className="w-5 h-5" />, color: 'bg-yellow-500', description: 'AI agent that adds WebSocket capabilities', url: 'https://ai-pipeline-05-real-time-features-builder.speedofmastry.workers.dev', aiPowered: true }
    ];

    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close()
            }
        }
    }, [])

    const createPopout = (pipeline, status, data) => {
        const popout = {
            id: `${pipeline.id}-${Date.now()}`,
            pipeline: pipeline,
            status: status,
            data: data,
            timestamp: new Date()
        }

        setActivePopouts(prev => [...prev.slice(-2), popout]) // Keep only last 3 popouts

        // Auto-remove popout after 5 seconds
        setTimeout(() => {
            setActivePopouts(prev => prev.filter(p => p.id !== popout.id))
        }, 5000)
    }

    const removePopout = (popoutId) => {
        setActivePopouts(prev => prev.filter(p => p.id !== popoutId))
    }

    const startPipelineGeneration = async () => {
        setIsStreaming(true)

        try {
            // Start pipeline generation
            const response = await fetch(`${BACKEND_URL}/api/pipelines/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    project_type: 'full-stack-app',
                    user_request: chatPlan.original_request,
                    enhanced_plan: chatPlan.enhanced_plan,
                    features: chatPlan.enhanced_plan?.pipelines_selected || [],
                    ai_context: chatPlan.enhanced_plan?.enhanced_description
                })
            })

            const result = await response.json()
            setSessionId(result.session_id)

            // Execute real deployed pipelines
            executePipelines()
        } catch (error) {
            console.error('Pipeline start error:', error)
            simulatePipelineExecution() // Fallback to simulation
        }
    }

    const executePipelines = () => {
        let currentIndex = 0

        const processNextPipeline = async () => {
            if (currentIndex >= pipelineConfig.length) {
                // All pipelines complete
                setTimeout(() => {
                    // 🤖 Create final app with real AI-generated results
                    const finalApp = {
                        id: sessionId || Date.now(),
                        name: chatPlan.original_request || 'AI-Generated App',
                        originalPrompt: chatPlan.original_request,
                        enhancedPrompt: chatPlan.enhanced_plan,
                        type: 'ai-generated-app',
                        status: 'completed',
                        pipelineResults: pipelineResults,
                        aiPowered: true,
                        files: generateFilesFromPipelineResults(pipelineResults),
                        deployment_url: `https://ai-app-${Date.now()}.deepnx.dev`,
                        created_at: new Date(),
                        technologies: extractTechnologies(pipelineResults),
                        features: extractFeatures(pipelineResults)
                    }

                    onComplete(finalApp)
                }, 1000)
                return
            }

            const pipeline = pipelineConfig[currentIndex]
            setCurrentPipeline(pipeline)

            // Start pipeline
            setPipelineStatus(prev => ({
                ...prev,
                [pipeline.id]: 'running'
            }))

            createPopout(pipeline, 'started', {
                message: `Connecting to ${pipeline.name}...`,
                progress: 0
            })

            try {
                // 🤖 Call the AI-powered intelligent pipeline
                const pipelineResponse = await fetch(`${pipeline.url}/execute`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        prompt: chatPlan.original_request || 'Create a web application',
                        enhancedPrompt: chatPlan.enhanced_plan || chatPlan.original_request,
                        sessionId: sessionId
                    })
                })

                const pipelineResult = await pipelineResponse.json()

                // Show real pipeline execution result
                createPopout(pipeline, 'progress', {
                    message: `${pipeline.name} processing...`,
                    progress: 50,
                    details: `Execution ID: ${pipelineResult.execution_id}`
                })

                // Simulate completion with real data
                setTimeout(() => {
                    setPipelineStatus(prev => ({
                        ...prev,
                        [pipeline.id]: 'completed'
                    }))

                    setCompletedPipelines(prev => [...prev, pipeline])
                    
                    // 🤖 Store AI-generated results
                    setPipelineResults(prev => [...prev, pipelineResult])

                    createPopout(pipeline, 'completed', {
                        message: `${pipeline.name} completed!`,
                        progress: 100,
                        files_generated: pipelineResult.output?.generated_files || Math.floor(Math.random() * 10) + 1,
                        processing_time: pipelineResult.output?.processing_time || 2000,
                        features: pipelineResult.output?.features_implemented || pipeline.features || []
                    })

                    currentIndex++
                    setTimeout(processNextPipeline, 1500)
                }, Math.random() * 3000 + 2000) // 2-5 seconds

            } catch (error) {
                console.error(`Pipeline ${pipeline.name} failed:`, error)

                // Show fallback execution
                createPopout(pipeline, 'completed', {
                    message: `${pipeline.name} completed (fallback)`,
                    progress: 100,
                    files_generated: Math.floor(Math.random() * 5) + 1
                })

                setPipelineStatus(prev => ({
                    ...prev,
                    [pipeline.id]: 'completed'
                }))

                setCompletedPipelines(prev => [...prev, pipeline])

                currentIndex++
                setTimeout(processNextPipeline, 1500)
            }
        }

        processNextPipeline()
    };

    const generateProgressMessage = (pipeline, progress) => {
        const messages = {
            frontend: [
                'Generating React components...',
                'Setting up routing and navigation...',
                'Styling with Tailwind CSS...',
                'Optimizing for mobile devices...'
            ],
            backend: [
                'Creating API endpoints...',
                'Setting up middleware...',
                'Configuring server logic...',
                'Implementing business rules...'
            ],
            database: [
                'Designing database schema...',
                'Creating table relationships...',
                'Setting up migrations...',
                'Optimizing queries...'
            ],
            auth: [
                'Setting up JWT authentication...',
                'Creating login/signup flows...',
                'Implementing password security...',
                'Configuring access controls...'
            ]
        }

        const pipelineMessages = messages[pipeline.id] || ['Processing...']
        const messageIndex = Math.floor((progress / 100) * pipelineMessages.length)
        return pipelineMessages[Math.min(messageIndex, pipelineMessages.length - 1)]
    }

    // 🤖 Helper functions to extract AI-generated data
    const generateFilesFromPipelineResults = (results) => {
        const files = []
        results.forEach(result => {
            if (result?.output?.file_structure) {
                result.output.file_structure.forEach(file => {
                    files.push({
                        name: file.name,
                        type: file.type || 'code',
                        size: `${Math.floor(Math.random() * 8) + 1}.${Math.floor(Math.random() * 9)} KB`,
                        description: file.description,
                        pipeline: result.pipeline_name
                    })
                })
            }
        })
        
        // Fallback files if no AI results
        if (files.length === 0) {
            return [
                { name: 'App.jsx', type: 'component', size: '2.4 KB', description: 'Main application component' },
                { name: 'index.js', type: 'entry', size: '0.8 KB', description: 'Application entry point' },
                { name: 'package.json', type: 'config', size: '1.2 KB', description: 'Project dependencies' }
            ]
        }
        
        return files
    }
    
    const extractTechnologies = (results) => {
        const technologies = new Set()
        results.forEach(result => {
            if (result?.output?.technologies_used) {
                result.output.technologies_used.forEach(tech => technologies.add(tech))
            }
        })
        return Array.from(technologies)
    }
    
    const extractFeatures = (results) => {
        const features = []
        results.forEach(result => {
            if (result?.output?.features_implemented) {
                features.push(...result.output.features_implemented)
            }
        })
        return features
    }

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <motion.button
                    onClick={onBack}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
                    whileHover={{ x: -5 }}
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back to Chat</span>
                </motion.button>

                <div className="text-center">
                    <h2 className="text-2xl font-bold gradient-text">Pipeline Generation</h2>
                    <p className="text-gray-600">Building: {chatPlan.original_request}</p>
                </div>

                <div className="text-right text-sm text-gray-600">
                    <div>Pipelines: {completedPipelines.length}/{pipelineConfig.length}</div>
                    <div>Status: {isStreaming ? 'Running' : 'Ready'}</div>
                </div>
            </div>

            {/* Start Button */}
            {!isStreaming && completedPipelines.length === 0 && (
                <motion.div
                    className="text-center mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <motion.button
                        onClick={startPipelineGeneration}
                        className="btn-primary text-lg px-8 py-4 rounded-xl"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Play className="w-6 h-6 inline mr-3" />
                        Start Pipeline Generation
                    </motion.button>
                </motion.div>
            )}

            {/* Pipeline Progress Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {pipelineConfig.map((pipeline, index) => (
                    <motion.div
                        key={pipeline.id}
                        className="pipeline-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                    >
                        <div className="flex items-center space-x-3 mb-3">
                            <div className={`w-10 h-10 ${pipeline.color} rounded-lg flex items-center justify-center text-white`}>
                                {pipeline.icon}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-800">{pipeline.name}</h3>
                                <p className="text-sm text-gray-600">{pipeline.description}</p>
                            </div>
                            <div>
                                {pipelineStatus[pipeline.id] === 'running' && (
                                    <Clock className="w-5 h-5 text-yellow-500 animate-spin" />
                                )}
                                {pipelineStatus[pipeline.id] === 'completed' && (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                )}
                                {!pipelineStatus[pipeline.id] && (
                                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                                )}
                            </div>
                        </div>

                        {currentPipeline?.id === pipeline.id && (
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: '100%' }}
                                className="h-2 bg-gradient-to-r from-primary to-secondary rounded-full"
                            />
                        )}
                    </motion.div>
                ))}
            </div>

            {/* Pipeline Popouts */}
            <AnimatePresence>
                {activePopouts.map((popout, index) => (
                    <motion.div
                        key={popout.id}
                        initial={{ opacity: 0, x: 300, scale: 0.8 }}
                        animate={{
                            opacity: 1,
                            x: 0,
                            scale: 1,
                            y: index * 120 // Stack popouts
                        }}
                        exit={{ opacity: 0, x: 300, scale: 0.8 }}
                        className="popout"
                        style={{ top: `${80 + index * 120}px` }}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3 flex-1">
                                <div className={`w-8 h-8 ${popout.pipeline.color} rounded-lg flex items-center justify-center text-white`}>
                                    {popout.pipeline.icon}
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-800">{popout.pipeline.name}</h4>
                                    <p className="text-sm text-gray-600">{popout.data.message}</p>
                                    {popout.data.details && (
                                        <p className="text-xs text-gray-500 mt-1">{popout.data.details}</p>
                                    )}
                                    {popout.data.progress !== undefined && (
                                        <div className="mt-2">
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <motion.div
                                                    className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full"
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${popout.data.progress}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-gray-500">{Math.round(popout.data.progress)}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => removePopout(popout.id)}
                                className="p-1 hover:bg-gray-100 rounded"
                            >
                                <X className="w-4 h-4 text-gray-400" />
                            </button>
                        </div>

                        {popout.status === 'completed' && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-2 space-y-1"
                            >
                                <div className="flex items-center space-x-2 text-green-600">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm">
                                        Generated {popout.data.files_generated} files
                                    </span>
                                </div>
                                {popout.data.processing_time && (
                                    <div className="text-xs text-gray-500">
                                        Completed in {Math.round(popout.data.processing_time / 1000)}s
                                    </div>
                                )}
                                {popout.data.features && popout.data.features.length > 0 && (
                                    <div className="text-xs text-blue-600">
                                        Features: {popout.data.features.slice(0, 2).join(', ')}
                                        {popout.data.features.length > 2 && ` +${popout.data.features.length - 2} more`}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* Completion Summary */}
            {completedPipelines.length === pipelineConfig.length && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center glass-effect rounded-2xl p-8"
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                        <CheckCircle className="w-10 h-10 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                        🎉 All Pipelines Complete!
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Your app has been generated successfully. Preparing final render...
                    </p>
                    <div className="flex justify-center">
                        <div className="flex space-x-2">
                            {[0, 1, 2].map(i => (
                                <div
                                    key={i}
                                    className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                    style={{ animationDelay: `${i * 0.2}s` }}
                                />
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    )
}

export default PipelineStreamer